import { ExternalLink, Trash2 } from 'lucide-react';
import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'v5/design-system/Button';
import { ImagePreview } from 'v5/design-system/ImagePreview';
import {
  MediaColumnLayout,
  MediaDialog,
  MediaDisplay,
  MediaThumbnails,
  MediaToolbar
} from 'v5/design-system/MediaDialog';
import { MediaKindIcon } from './MediaKindIcon';
import type { MediaGalleryItem } from './MediaGallery';
import { mediaKindOf } from './uploadedMedia';

/**
 * One file of a collection at full size, with the previous / next one a
 * click or an arrow key away. Not an image: its icon and a link to open it.
 * Follows its file by key as the collection changes; when that file goes,
 * the one taking its place shows.
 */
export function MediaLightbox({
  items,
  currentKey,
  onCurrentKeyChange,
  onRemove,
  removing,
  thumbnailsShown,
  onThumbnailsShownChange
}: {
  items: MediaGalleryItem[];
  /** The file shown; `null` = closed. */
  currentKey: string | null;
  onCurrentKeyChange(key: string | null): void;
  /** Absent when the field is read-only. */
  onRemove?(item: MediaGalleryItem): void;
  /** Files whose removal is under way: no second Remove for them. */
  removing: ReadonlySet<string>;
  /** The strip of thumbnails under the file; a user's preference the host keeps. */
  thumbnailsShown: boolean;
  onThumbnailsShownChange(shown: boolean): void;
}) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'ImageCollectionUploadField'
  });
  const count = items.length;
  const lastIndex = useRef(0);
  const index = (() => {
    if (currentKey === null || count === 0) return null;
    const found = items.findIndex(item => item.key === currentKey);
    if (found >= 0) return found;
    // Gone while shown: whichever file now stands where it stood.
    return Math.min(lastIndex.current, count - 1);
  })();
  useLayoutEffect(() => {
    if (index !== null) lastIndex.current = index;
  });
  const media = index === null ? undefined : items[index];
  const go = (step: number) => {
    if (index === null) return;
    onCurrentKeyChange(items[(index + step + count) % count].key);
  };

  return (
    <MediaDialog
      open={media !== undefined}
      onOpenChange={open => {
        if (!open) onCurrentKeyChange(null);
      }}
    >
      {media !== undefined && index !== null && (
        <>
          <MediaDialog.Scene
            subject={media.key}
            backdrop={mediaKindOf(media) === 'image' ? media.src : null}
          >
            {mediaKindOf(media) === 'image' ? (
              <MediaDisplay.Picture src={media.src} alt={media.name} />
            ) : (
              <div className="flex flex-col items-center gap-4 text-fg-default/80">
                <MediaKindIcon media={media} className="size-16" />
                <a
                  href={media.src}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm underline underline-offset-4"
                >
                  <ExternalLink className="size-4" />
                  {t('open')}
                </a>
              </div>
            )}
          </MediaDialog.Scene>
          <MediaColumnLayout>
            <MediaDisplay>
              {count > 1 && (
                <MediaDisplay.Navigation
                  onPrevious={() => go(-1)}
                  onNext={() => go(1)}
                />
              )}
              <MediaDisplay.Close />
            </MediaDisplay>
            {count > 1 && (
              <MediaThumbnails
                shown={thumbnailsShown}
                aria-label={t('listLabel')}
              >
                {items.map((item, itemIndex) => (
                  <MediaThumbnails.Item
                    key={item.key}
                    current={itemIndex === index}
                    aria-label={item.name}
                    onClick={() => onCurrentKeyChange(item.key)}
                  >
                    {mediaKindOf(item) === 'image' ? (
                      <ImagePreview src={item.src} className="bg-transparent" />
                    ) : (
                      <MediaKindIcon
                        media={item}
                        className="p-3 text-fg-default/80"
                      />
                    )}
                  </MediaThumbnails.Item>
                ))}
              </MediaThumbnails>
            )}
            <MediaToolbar>
              <MediaToolbar.Name>{media.name}</MediaToolbar.Name>
              <MediaToolbar.Counter index={index} count={count} />
              <MediaToolbar.Actions>
                {count > 1 && (
                  <MediaThumbnails.Toggle
                    shown={thumbnailsShown}
                    onShownChange={onThumbnailsShownChange}
                  />
                )}
                {onRemove && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('remove', { name: media.name })}
                    disabled={removing.has(media.key)}
                    onClick={() => onRemove(media)}
                  >
                    <Trash2 />
                    {t('removeShort')}
                  </Button>
                )}
              </MediaToolbar.Actions>
            </MediaToolbar>
          </MediaColumnLayout>
        </>
      )}
    </MediaDialog>
  );
}
